import reloadRightsManagementLicenseData from '../actions/reloadRightsManagementLicenseData';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc, { format } from 'owa-localize';
import { infoLinkClickHere } from 'owa-locstrings/lib/strings/infolinkclickhere.locstring.json';
import { irm_templateInfo } from 'owa-locstrings/lib/strings/irm_templateinfo.locstring.json';
import ItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemViewState';
import hasRevokePermission from 'owa-mail-revocation/lib/utils/hasRevokePermission';
import RightsManagementFailureCode from 'owa-mail-store/lib/store/schema/RightsManagementFailureCode';
import Message from 'owa-service/lib/contract/Message';
import RightsManagementLicenseDataType from 'owa-service/lib/contract/RightsManagementLicenseDataType';
import {
    rpEncryptMessage,
    rpEncryptMessageDNF,
    rpEncryptionTips,
    irm_reloadMessage,
} from './createIRMInfoBarMessage.locstring.json';
import {
    InfoBarCustomAction,
    InfoBarMessageColor,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import {
    ENCRYPT_GUID,
    ENCRYPT_DNF_GUID,
    ENCRYPT_DNRA_GUID,
} from 'owa-mail-compose-actions/lib/store/schema/EncryptionGUID';

const ENCRYPT_HOWTO = 'https://go.microsoft.com/fwlink/?linkid=867645';

export default function tryCreateIRMInfoBarMessage(
    message: Message,
    key: string,
    itemViewState: ItemViewState
): InfoBarMessageViewState {
    const IRMData = message.RightsManagementLicenseData;
    const showIRMInfobar =
        itemViewState &&
        !hasRevokePermission(itemViewState.omeMessageState) &&
        (isAdhocEncryption(IRMData) || !isFeatureEnabled('cmp-clp') || !message.MSIPLabelGuid);

    if (IRMData && showIRMInfobar) {
        if (
            !IRMData.RightsManagedMessageDecryptionStatus ||
            IRMData.RightsManagedMessageDecryptionStatus == RightsManagementFailureCode.Success
        ) {
            // If the decryption was successful, simply display the template information
            return <InfoBarMessageViewState>{
                key: key,
                source: InfoBarMessageSource.ReadingPane,
                rank: isFeatureEnabled('cmp-encryption')
                    ? InfoBarMessageRank.Encryption
                    : InfoBarMessageRank.Info,
                color: InfoBarMessageColor.Grey,
                messageParts: isFeatureEnabled('cmp-encryption')
                    ? createMessageParts(IRMData)
                    : [
                          format(
                              loc(irm_templateInfo),
                              IRMData.TemplateName,
                              IRMData.TemplateDescription
                          ),
                      ],
            };
        } else if (
            IRMData.RightsManagedMessageDecryptionStatus ==
            RightsManagementFailureCode.MissingLicense
        ) {
            // If decryption failed due to a missing license, display the option to re-load the item
            const reloadAction: InfoBarCustomAction = {
                action: () => {
                    reloadRightsManagementLicenseData(itemViewState, message.ItemId.Id);
                },
                text: loc(infoLinkClickHere),
            };

            return <InfoBarMessageViewState>{
                key: key,
                source: InfoBarMessageSource.ReadingPane,
                rank: InfoBarMessageRank.Info,
                color: InfoBarMessageColor.Grey,
                messageParts: [loc(irm_reloadMessage), reloadAction],
            };
        }
    }

    return null;
}

function createMessageParts(IRMData: RightsManagementLicenseDataType) {
    let messageParts: (string | InfoBarCustomAction)[] = [];
    switch (IRMData.RmsTemplateId) {
        case ENCRYPT_GUID:
            messageParts = [loc(rpEncryptMessage), getEncryptionTips()];
            break;

        case ENCRYPT_DNF_GUID:
            messageParts = [loc(rpEncryptMessageDNF), getEncryptionTips()];
            break;

        default:
            messageParts = [
                format(loc(irm_templateInfo), IRMData.TemplateName, IRMData.TemplateDescription),
            ];
    }

    return messageParts;
}

function getEncryptionTips(): InfoBarCustomAction {
    return {
        text: loc(rpEncryptionTips),
        action: () => {
            window.open(ENCRYPT_HOWTO, '_blank');
        },
    };
}

function isAdhocEncryption(IRMData: RightsManagementLicenseDataType): Boolean {
    return (
        IRMData &&
        (IRMData.RmsTemplateId == ENCRYPT_GUID ||
            IRMData.RmsTemplateId == ENCRYPT_DNF_GUID ||
            IRMData.RmsTemplateId == ENCRYPT_DNRA_GUID)
    );
}
